/** Column definitions for the External access grid (and its embedded twin).
 *
 * All columns are `filterable: false` — filtering is driven entirely by the QuerySearch
 * widget (the page also sets `disableColumnFilter`). Only `created_ts` and `id` are
 * sortable: they are the backend's whole ordering whitelist. Cell popovers carry copy
 * actions, links to the records a call names (its key, partner, user, tenant) and quick
 * filters — `onQuickFilter` appends an equality chip to the search.
 *
 * A row outlives its key, user and partner: a ref is null when the record is gone while its
 * `*_id` stays, so those cells fall back to the bare id (still filterable, never linked).
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import type { ExternalAccessItem } from '@/api/externalAccess';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { StatusCodeChip, formatMs } from '@/features/api-requests/apiRequestFormat';
import { TsValue } from '@/features/bgtasks/taskFormat';

import {
  ChannelChip,
  OutcomeChip,
  CHANNEL_LABELS,
  OUTCOME_LABELS,
  PRINCIPAL_LABELS,
  clientOf,
} from './externalAccessFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

function copyAction(label: string, value: string): CellAction {
  return {
    key: `copy:${label}`,
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function openAction(label: string, href: string): CellAction {
  return { key: `open:${href}`, label, icon: <OpenInNewIcon fontSize="small" />, href };
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an equality `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Open a row's detail drawer. */
  onOpen: (row: ExternalAccessItem) => void;
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter:${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

/** Who made the call, as one line of text (also the CSV value). */
export function principalText(row: ExternalAccessItem): string {
  if (row.principal_kind === 'api_key' || (!row.principal_kind && row.credential_id != null)) {
    if (row.credential) {
      return row.credential.key_prefix
        ? `${row.credential.name} · ${row.credential.key_prefix}…`
        : row.credential.name;
    }
    return row.credential_id != null ? `Key #${row.credential_id} (deleted)` : 'API key';
  }
  if (row.principal_kind === 'oauth_user') {
    if (row.user) return row.user.email;
    return row.user_id != null ? `User #${row.user_id} (deleted)` : 'OAuth user';
  }
  if (row.principal_kind === 'ventrata_token') return PRINCIPAL_LABELS.ventrata_token;
  return '';
}

function principalActions(row: ExternalAccessItem, onQuickFilter: BuildColumnsOptions['onQuickFilter']): CellAction[] {
  const out: CellAction[] = [];
  if (row.credential_id != null) {
    if (row.credential) out.push(openAction('Open API credential', `/api-credentials/${row.credential.id}`));
    out.push(filterAction('Filter by this key', 'credential', String(row.credential_id), onQuickFilter));
    if (row.credential?.key_prefix) out.push(copyAction('Copy key prefix', row.credential.key_prefix));
  }
  if (row.user_id != null) {
    if (row.user) out.push(openAction('Open user', `/users/${row.user.id}`));
    out.push(filterAction('Filter by this user', 'user', String(row.user_id), onQuickFilter));
    if (row.user) out.push(copyAction('Copy e-mail', row.user.email));
  }
  if (row.principal_kind) {
    out.push(
      filterAction(
        `Filter: ${PRINCIPAL_LABELS[row.principal_kind]} calls`,
        'principal',
        row.principal_kind,
        onQuickFilter,
      ),
    );
  }
  return out;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<ExternalAccessItem>[] {
  const { showTenant, onQuickFilter, onOpen } = opts;

  const cols: GridColDef<ExternalAccessItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Show call details">
          <IconButton
            size="small"
            aria-label="Show call details"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(p.row);
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'created_ts',
      headerName: 'Time',
      width: 175,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.created_ts}
          actions={[
            copyAction('Copy timestamp', p.row.created_ts),
            // created's equality operator matches a whole UTC day.
            filterAction('Filter by this day (UTC)', 'created', p.row.created_ts.slice(0, 10), onQuickFilter),
          ]}
        >
          <TsValue value={p.row.created_ts} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'channel',
      headerName: 'Channel',
      width: 100,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => CHANNEL_LABELS[row.channel] ?? row.channel,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this channel', 'channel', p.row.channel, onQuickFilter)]}
        >
          <ChannelChip channel={p.row.channel} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      width: 120,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => OUTCOME_LABELS[row.outcome] ?? row.outcome,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this outcome', 'outcome', p.row.outcome, onQuickFilter)]}
        >
          <OutcomeChip outcome={p.row.outcome} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      filterable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) =>
        p.row.status ? (
          <CellActionsPopover
            auto
            actions={[filterAction('Filter by this status', 'status', String(p.row.status), onQuickFilter)]}
          >
            <StatusCodeChip code={p.row.status} />
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 80,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.method ? <Box component="span" sx={mono}>{p.row.method}</Box> : dash),
    },
    {
      field: 'operation',
      headerName: 'Operation',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.operation}
          actions={[
            copyAction('Copy operation', p.row.operation),
            filterAction('Filter by this operation', 'operation', p.row.operation, onQuickFilter),
          ]}
          sx={ellipsis}
        >
          <Box component="span" sx={mono}>{p.row.operation}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'principal',
      headerName: 'Principal',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => principalText(row),
      renderCell: (p) => {
        const text = principalText(p.row);
        if (!text) return dash;
        const gone =
          (p.row.credential_id != null && !p.row.credential && p.row.principal_kind !== 'oauth_user') ||
          (p.row.principal_kind === 'oauth_user' && p.row.user_id != null && !p.row.user);
        return (
          <CellActionsPopover
            auto
            title={p.row.principal_kind ? PRINCIPAL_LABELS[p.row.principal_kind] : undefined}
            actions={principalActions(p.row, onQuickFilter)}
            sx={ellipsis}
          >
            <Box component="span" sx={gone ? { color: 'text.secondary', fontStyle: 'italic' } : undefined}>
              {text}
            </Box>
          </CellActionsPopover>
        );
      },
    },
    {
      field: 'partner',
      headerName: 'Partner',
      width: 130,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.partner?.code ?? (row.partner_id != null ? `#${row.partner_id}` : ''),
      renderCell: (p) => {
        if (p.row.partner_id == null) return dash;
        return (
          <CellActionsPopover
            auto
            title={p.row.partner?.name ?? 'Partner no longer exists'}
            actions={[
              ...(p.row.partner ? [openAction('Open partner', `/partners/${p.row.partner.id}`)] : []),
              // All digits: the partner namespace's iexact also matches the id.
              filterAction('Filter by this partner', 'partner', String(p.row.partner_id), onQuickFilter),
            ]}
            sx={ellipsis}
          >
            <Box component="span" sx={mono}>{p.row.partner?.code ?? `#${p.row.partner_id}`}</Box>
          </CellActionsPopover>
        );
      },
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 140,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tenant?.code ?? (row.tenant_id != null ? `#${row.tenant_id}` : ''),
      renderCell: (p) => {
        if (p.row.tenant_id == null) return dash;
        return (
          <WithTenantMark environment={p.row.tenant?.environment}>
            <CellActionsPopover
              auto
              title={p.row.tenant?.name ?? 'Tenant no longer exists'}
              actions={[
                ...(p.row.tenant ? [openAction('Open tour operator', `/tour-operators/${p.row.tenant.id}`)] : []),
                filterAction('Filter by this tenant', 'tenant', String(p.row.tenant_id), onQuickFilter),
              ]}
              sx={ellipsis}
            >
              <Box component="span" sx={mono}>{p.row.tenant?.code ?? `#${p.row.tenant_id}`}</Box>
            </CellActionsPopover>
          </WithTenantMark>
        );
      },
    });
  }

  cols.push(
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 90,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMs(p.row.duration_ms ?? null)}
        </Box>
      ),
    },
    {
      field: 'ip',
      headerName: 'IP',
      width: 130,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.ip ? (
          <CellActionsPopover
            auto
            actions={[
              copyAction('Copy IP', p.row.ip),
              filterAction('Filter by this IP', 'ip', p.row.ip, onQuickFilter),
            ]}
          >
            <Box component="span" sx={mono}>{p.row.ip}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'error_code',
      headerName: 'Error code',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.error_code ? (
          <CellActionsPopover
            auto
            title={p.row.error_code}
            actions={[
              copyAction('Copy error code', p.row.error_code),
              filterAction('Filter by this error code', 'error_code', p.row.error_code, onQuickFilter),
            ]}
            sx={ellipsis}
          >
            <Box component="span" sx={{ ...mono, color: 'error.main' }}>{p.row.error_code}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'request_id',
      headerName: 'Request ID',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.request_id ? (
          <CellActionsPopover
            auto
            title={p.row.request_id}
            actions={[
              copyAction('Copy request ID', p.row.request_id),
              filterAction('Filter by this request', 'request', p.row.request_id, onQuickFilter),
            ]}
            sx={ellipsis}
          >
            <Box component="span" sx={mono}>{p.row.request_id}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 160,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => clientOf(row),
      renderCell: (p) => {
        const client = clientOf(p.row);
        return client ? (
          <Box component="span" sx={ellipsis} title={client}>
            {client}
          </Box>
        ) : (
          dash
        );
      },
    },
    {
      field: 'user_agent',
      headerName: 'User agent',
      width: 200,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.user_agent ? (
          <Box component="span" sx={ellipsis} title={p.row.user_agent}>
            {p.row.user_agent}
          </Box>
        ) : (
          dash
        ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover auto actions={[copyAction('Copy row id', String(p.row.id))]}>
          <Box component="span" sx={mono}>{p.row.id}</Box>
        </CellActionsPopover>
      ),
    },
  );

  return cols;
}
